import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DashboardShell } from '../../components/DashboardShell.js';
import { BannerUploader } from '../../components/BannerUploader.js';
import { RichTextEditor } from '../../components/RichTextEditor.js';
import { api, ApiClientError } from '../../lib/api.js';
import { useCurrentOrg, useOrgDetails, useStripeRefresh } from '../../lib/orgs.js';
import type { DashboardTicketType } from '../../lib/tickets.js';
import { OnboardingPage } from './OnboardingPage.js';
import { TicketTypesManager } from './TicketTypesManager.js';
import { ValidatorsPanel } from './ValidatorsPanel.js';

type Locale = 'es' | 'en';

interface EventDoc {
  id: string;
  orgId: string;
  orgSlug: string;
  orgName: string;
  stripeOnboardingComplete: boolean;
  slug: string;
  status: 'draft' | 'live' | 'ended' | 'cancelled';
  bannerUrl: string | null;
  startsAt: string;
  endsAt: string;
  timezone: string;
  defaultLocale: Locale;
  supportedLocales: Locale[];
  translations: Record<string, { title: string; description?: string; venueLabel?: string }>;
  currency: string;
  serviceFeePct: number;
  publishedAt: string | null;
  ticketTypes: DashboardTicketType[];
}

const COMMON_TZ = [
  'America/Mexico_City',
  'America/Monterrey',
  'America/Tijuana',
  'America/Cancun',
  'America/Bogota',
  'America/Lima',
  'America/Santiago',
  'America/Buenos_Aires',
  'America/Guayaquil',
  'America/New_York',
  'America/Los_Angeles',
  'Europe/Madrid',
  'UTC',
];

export function EditEventPage() {
  const { t } = useTranslation('dashboard');
  const { lang, id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const org = useCurrentOrg();
  const orgDetails = useOrgDetails(org?.id ?? null);

  const eventQuery = useQuery<EventDoc>({
    queryKey: ['event', id],
    queryFn: () => api<EventDoc>(`/events/${id}`),
    enabled: Boolean(id),
    refetchOnWindowFocus: false,
  });

  // Si el evento dice "Stripe no conectado" hacemos un pull silencioso a la
  // verdad de Stripe — el webhook `account.updated` a veces llega tarde (o
  // no llega si el endpoint del usuario no tiene ese evento suscrito), así
  // que confiar sólo en el DB local mostraba el warning indefinidamente.
  const stripeRefresh = useStripeRefresh(org?.id ?? null);
  const autoRefreshedRef = useRef(false);
  useEffect(() => {
    if (autoRefreshedRef.current) return;
    if (!org?.id) return;
    if (!eventQuery.data) return;
    if (eventQuery.data.stripeOnboardingComplete) return;
    autoRefreshedRef.current = true;
    stripeRefresh.mutate();
    // El éxito invalida ['event'] (lo hace el hook) → eventQuery se refetch
    // solo y stripeOnboardingComplete queda actualizado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org?.id, eventQuery.data?.stripeOnboardingComplete]);

  // Local form state, hydrated once the event arrives.
  const [hydrated, setHydrated] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [venue, setVenue] = useState('');
  const [bannerUrl, setBannerUrl] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [timezone, setTimezone] = useState('America/Mexico_City');
  const [serviceFeePct, setServiceFeePct] = useState(0);
  const [editLocale, setEditLocale] = useState<Locale>('es');

  // La moneda es read-only: queda fijada al país de la org (default
  // settlement currency de su cuenta Stripe Connect) y cambiarla rompería
  // el checkout, así que el server rechaza el patch si se intenta.
  const currency = eventQuery.data?.currency ?? '';

  useEffect(() => {
    if (eventQuery.data && !hydrated) {
      const e = eventQuery.data;
      const loc: Locale = e.defaultLocale;
      setEditLocale(loc);
      const tr = e.translations[loc] ?? Object.values(e.translations)[0] ?? { title: '' };
      setTitle(tr.title ?? '');
      setDescription(tr.description ?? '');
      setVenue(tr.venueLabel ?? '');
      setBannerUrl(e.bannerUrl ?? '');
      setStartsAt(toLocalDatetimeInput(new Date(e.startsAt)));
      setEndsAt(toLocalDatetimeInput(new Date(e.endsAt)));
      setTimezone(e.timezone);
      setServiceFeePct(e.serviceFeePct);
      setHydrated(true);
    }
  }, [eventQuery.data, hydrated]);

  // Switching the locale tab pre-fills with whatever translation we have
  // for that locale so the user can edit it without losing the original.
  function switchLocale(next: Locale) {
    if (!eventQuery.data) return;
    // Snapshot the current edits into the in-memory translations map.
    const map = { ...eventQuery.data.translations };
    map[editLocale] = {
      title: title.trim(),
      description: description.trim(),
      venueLabel: venue.trim(),
    };
    queryClient.setQueryData<EventDoc>(['event', id], {
      ...eventQuery.data,
      translations: map,
    });
    const nextTr = map[next] ?? { title: '', description: '', venueLabel: '' };
    setTitle(nextTr.title ?? '');
    setDescription(nextTr.description ?? '');
    setVenue(nextTr.venueLabel ?? '');
    setEditLocale(next);
  }

  const save = useMutation({
    mutationFn: () => {
      const map = {
        ...(eventQuery.data?.translations ?? {}),
        [editLocale]: {
          title: title.trim(),
          description: description.trim(),
          venueLabel: venue.trim(),
        },
      };
      return api<{ ok: true }>(`/events/${id}`, {
        method: 'PATCH',
        body: {
          bannerUrl: bannerUrl || null,
          startsAt: new Date(startsAt).toISOString(),
          endsAt: new Date(endsAt).toISOString(),
          timezone,
          serviceFeePct,
          translations: map,
          supportedLocales: Array.from(
            new Set([...(eventQuery.data?.supportedLocales ?? []), editLocale]),
          ),
        },
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['event', id] }),
  });

  const publish = useMutation({
    mutationFn: () => api<{ ok: true }>(`/events/${id}/publish`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['event', id] }),
  });

  const unpublish = useMutation({
    mutationFn: () => api<{ ok: true }>(`/events/${id}/unpublish`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['event', id] }),
  });

  if (!org) return <OnboardingPage />;

  if (eventQuery.isLoading || !hydrated) {
    return (
      <DashboardShell>
        <div className="text-neutral-500 text-sm">{t('event_editor.loading')}</div>
      </DashboardShell>
    );
  }
  if (eventQuery.isError || !eventQuery.data) {
    return (
      <DashboardShell>
        <div className="card text-center text-neutral-400">
          <h2 className="text-xl font-semibold mb-2">{t('event_editor.not_found_title')}</h2>
          <Link to={`/${lang}/dashboard/events`} className="btn-secondary mt-4 inline-flex">
            {t('event_editor.back_to_events')}
          </Link>
        </div>
      </DashboardShell>
    );
  }

  const event = eventQuery.data;
  const stripeOk = event.stripeOnboardingComplete;
  const allTicketTypes = event.ticketTypes ?? [];
  const visibleTicketTypes = allTicketTypes.filter((tt) => !tt.hidden);
  const hasTicketTypes = visibleTicketTypes.length > 0;
  // Eventos RSVP (todos los tiers gratis, incluso los ocultos) se pueden
  // publicar sin Stripe — espeja la regla del API en POST /:id/publish.
  const allFree = allTicketTypes.length > 0 && allTicketTypes.every((tt) => tt.priceCents === 0);
  // Gate de revisión de la org: no se puede publicar hasta estar aprobada. Si
  // todavía no cargó el detalle, no bloqueamos (el server valida igual). Sólo
  // `pending`/`rejected` explícitos frenan.
  const orgApproved = orgDetails.data
    ? orgDetails.data.reviewStatus !== 'pending' && orgDetails.data.reviewStatus !== 'rejected'
    : true;
  const canPublish =
    event.status === 'draft' && (stripeOk || allFree) && hasTicketTypes && orgApproved;
  const canUnpublish = event.status === 'live';
  const publicUrl = `${window.location.origin}/${lang}/o/${event.orgSlug}/${event.slug}`;

  return (
    <DashboardShell>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="min-w-0">
          <Link
            to={`/${lang}/dashboard/events`}
            className="text-sm text-neutral-500 hover:text-neutral-300"
          >
            ← {t('event_editor.back_short')}
          </Link>
          <h1 className="text-3xl font-bold mt-1 break-words">{title || event.slug}</h1>
          <p className="text-xs text-neutral-500 mt-1 font-mono break-all">/{event.slug}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <StatusBadge status={event.status} />
          <Link
            to={`/${lang}/dashboard/events/${event.id}/sales`}
            className="btn-secondary text-sm whitespace-nowrap"
          >
            {t('event_editor.view_sales')}
          </Link>
        </div>
      </div>

      {event.status === 'live' && (
        <div className="card mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-neutral-500 mb-1">
              {t('event_editor.public_url')}
            </p>
            <a
              href={publicUrl}
              target="_blank"
              rel="noreferrer"
              className="text-neutral-200 hover:text-[var(--color-accent)] break-all text-sm"
            >
              {publicUrl}
            </a>
          </div>
          <button
            type="button"
            onClick={() => navigator.clipboard.writeText(publicUrl)}
            className="btn-secondary text-sm whitespace-nowrap"
          >
            {t('event_editor.copy')}
          </button>
        </div>
      )}

      {!stripeOk && (
        <StripeNotReadyBanner
          lang={lang ?? 'es'}
          refresh={stripeRefresh}
          autoRefreshed={autoRefreshedRef.current}
        />
      )}

      <form
        className="space-y-5 max-w-2xl"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <LocaleTabs
          supported={event.supportedLocales}
          current={editLocale}
          onChange={switchLocale}
        />

        <Field label={t('event_editor.title_label')}>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            minLength={2}
            maxLength={160}
            className="input"
          />
        </Field>

        <Field label={t('event_editor.description_label')}>
          <RichTextEditor
            value={description}
            onChange={setDescription}
            rows={5}
            maxLength={8000}
            labels={{
              bold: t('event_editor.rich_text.bold'),
              italic: t('event_editor.rich_text.italic'),
              hint: t('event_editor.rich_text.hint'),
            }}
          />
        </Field>

        <Field label={t('event_editor.venue_label')}>
          <input
            type="text"
            value={venue}
            onChange={(e) => setVenue(e.target.value)}
            maxLength={160}
            className="input"
          />
        </Field>

        <Field label={t('event_editor.banner_label')} hint={t('event_editor.banner_help')}>
          <BannerUploader orgId={event.orgId} value={bannerUrl} onChange={setBannerUrl} />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t('event_editor.starts_at_label')}>
            <input
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              required
              className="input"
            />
          </Field>
          <Field label={t('event_editor.ends_at_label')}>
            <input
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              required
              className="input"
            />
          </Field>
        </div>

        <Field label={t('event_editor.timezone_label')}>
          <select value={timezone} onChange={(e) => setTimezone(e.target.value)} className="input">
            {COMMON_TZ.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t('event_editor.currency_label')} hint={t('event_editor.currency_help')}>
            <input type="text" value={currency} readOnly disabled className="input opacity-60" />
          </Field>
          <Field
            label={t('event_editor.service_fee_label')}
            hint={t('event_editor.service_fee_help')}
          >
            <input
              type="number"
              min={0}
              max={50}
              step={0.5}
              value={serviceFeePct}
              onChange={(e) => setServiceFeePct(Number(e.target.value))}
              className="input"
            />
          </Field>
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-2">
          <button type="submit" disabled={save.isPending} className="btn-primary">
            {save.isPending ? t('event_editor.saving') : t('event_editor.save')}
          </button>

          {event.status === 'draft' && (
            <button
              type="button"
              onClick={() => publish.mutate()}
              disabled={!canPublish || publish.isPending}
              className="btn-secondary"
              title={
                !orgApproved
                  ? t('event_editor.review_needed_title')
                  : !hasTicketTypes
                    ? t('event_editor.tickets_needed_title')
                    : !stripeOk && !allFree
                      ? t('event_editor.stripe_needed_title')
                      : undefined
              }
            >
              {publish.isPending ? t('event_editor.publishing') : t('event_editor.publish')}
            </button>
          )}

          {canUnpublish && (
            <button
              type="button"
              onClick={() => unpublish.mutate()}
              disabled={unpublish.isPending}
              className="btn-secondary"
            >
              {unpublish.isPending ? t('event_editor.unpublishing') : t('event_editor.unpublish')}
            </button>
          )}

          <button
            type="button"
            onClick={() => navigate(`/${lang}/dashboard/events`)}
            className="text-sm text-neutral-400 hover:text-neutral-200 ml-auto"
          >
            {t('event_editor.back')}
          </button>
        </div>

        {save.isSuccess && !save.isPending && (
          <p className="text-emerald-400 text-sm">{t('event_editor.saved_ok')}</p>
        )}
        {save.isError && (
          <p className="text-red-400 text-sm">{(save.error as ApiClientError).message}</p>
        )}
        {publish.isError && (
          <p className="text-red-400 text-sm">{(publish.error as ApiClientError).message}</p>
        )}
        {unpublish.isError && (
          <p className="text-red-400 text-sm">{(unpublish.error as ApiClientError).message}</p>
        )}
      </form>

      {event.status === 'draft' && !orgApproved && (
        <div className="mt-8 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-200 px-4 py-3 text-sm max-w-2xl">
          {orgDetails.data?.reviewStatus === 'rejected'
            ? t('event_editor.review_rejected_warning')
            : t('event_editor.review_pending_warning')}
        </div>
      )}

      {event.status === 'draft' && !hasTicketTypes && (
        <div className="mt-8 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-200 px-4 py-3 text-sm max-w-2xl">
          {t('event_editor.needs_tickets_warning')}
        </div>
      )}

      <TicketTypesManager
        eventId={event.id}
        currency={event.currency}
        defaultLocale={event.defaultLocale}
        editLocale={editLocale}
        ticketTypes={event.ticketTypes ?? []}
      />

      <div className="mt-8">
        <ValidatorsPanel eventId={event.id} lang={lang ?? 'es'} />
      </div>
    </DashboardShell>
  );
}

function LocaleTabs({
  supported,
  current,
  onChange,
}: {
  supported: Locale[];
  current: Locale;
  onChange: (l: Locale) => void;
}) {
  const { t } = useTranslation('dashboard');
  // Mostramos primero los locales que el evento ya soporta, luego los que se
  // pueden agregar. Así el productor que creó el evento sólo en EN ve
  // "English" primero, y "Español agregar" después — el tab activo siempre
  // queda visualmente al inicio sin tener que rebuscarlo.
  const orderedSupported: Locale[] = ['es', 'en'].filter((l): l is Locale =>
    supported.includes(l as Locale),
  );
  const orderedAddable: Locale[] = ['es', 'en'].filter(
    (l): l is Locale => !supported.includes(l as Locale),
  );
  const all: Locale[] = [...orderedSupported, ...orderedAddable];
  return (
    <div className="flex items-center gap-1 border-b border-neutral-900">
      {all.map((l) => {
        const has = supported.includes(l);
        const active = current === l;
        return (
          <button
            key={l}
            type="button"
            onClick={() => onChange(l)}
            className={[
              'px-3 py-2 text-sm border-b-2 -mb-px transition',
              active
                ? 'border-[var(--color-accent)] text-neutral-100'
                : 'border-transparent text-neutral-500 hover:text-neutral-300',
            ].join(' ')}
          >
            {l === 'es' ? 'Español' : 'English'}
            {!has && (
              <span className="ml-2 text-[10px] uppercase tracking-wider text-neutral-600">
                {t('event_editor.locale_add_badge')}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function StatusBadge({ status }: { status: EventDoc['status'] }) {
  const { t } = useTranslation('dashboard');
  const classByStatus: Record<EventDoc['status'], string> = {
    draft: 'bg-neutral-700/40 text-neutral-300 border-neutral-700',
    live: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    ended: 'bg-neutral-900 text-neutral-500 border-neutral-800',
    cancelled: 'bg-red-500/10 text-red-300 border-red-500/30',
  };
  return (
    <span
      className={`text-xs px-3 py-1 rounded-full border whitespace-nowrap ${classByStatus[status]}`}
    >
      {t(`event_editor.status_badge.${status}`)}
    </span>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-sm text-neutral-400">{label}</span>
      <div className="mt-2">{children}</div>
      {hint && <p className="text-xs text-neutral-500 mt-1">{hint}</p>}
    </label>
  );
}

function toLocalDatetimeInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function StripeNotReadyBanner({
  lang,
  refresh,
  autoRefreshed,
}: {
  lang: string;
  refresh: ReturnType<typeof useStripeRefresh>;
  autoRefreshed: boolean;
}) {
  const { t } = useTranslation('dashboard');
  // Tres estados: nunca conectaste / conectaste pero Stripe verifica /
  // status raro. Los derivamos del resultado del refresh, que es la verdad
  // de Stripe (no del cache local de Boletra).
  const result = refresh.data;
  const hasAccount = Boolean(result?.stripeAccountId);
  const pending = result?.pendingVerification === true;

  let title: string;
  let body: string;
  if (refresh.isPending || (!result && autoRefreshed)) {
    title = t('event_editor.stripe_banner.verifying_title');
    body = t('event_editor.stripe_banner.verifying_body');
  } else if (pending) {
    title = t('event_editor.stripe_banner.pending_title');
    body = t('event_editor.stripe_banner.pending_body');
  } else if (hasAccount) {
    title = t('event_editor.stripe_banner.missing_title');
    body = t('event_editor.stripe_banner.missing_body');
  } else {
    title = t('event_editor.stripe_banner.connect_title');
    body = t('event_editor.stripe_banner.connect_body');
  }

  return (
    <div className="mb-6 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-200 px-4 py-3 text-sm">
      <p className="font-semibold mb-1">{title}</p>
      <p className="text-amber-200/90 mb-3">{body}</p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
          className="inline-flex items-center px-3 py-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 transition text-xs font-medium disabled:opacity-50"
        >
          {refresh.isPending
            ? t('event_editor.stripe_banner.rechecking')
            : t('event_editor.stripe_banner.recheck')}
        </button>
        <Link
          to={`/${lang}/dashboard/settings`}
          className="inline-flex items-center px-3 py-1.5 rounded-md border border-amber-500/40 hover:bg-amber-500/10 transition text-xs font-medium"
        >
          {t('event_editor.stripe_banner.go_to_settings')}
        </Link>
      </div>
      {refresh.isError && <p className="text-red-400 text-xs mt-2">{refresh.error.message}</p>}
    </div>
  );
}
